import { Link } from "react-router-dom";
import { ShieldAlert, MapPin } from "lucide-react";
import PageMeta from "./PageMeta";

export default function AnglerSafetyPage() {
  return (
    <>
      <PageMeta
        title="Angler Safety & what3words | Redruth Sea Angling Association"
        description="Practical angler safety advice for Cornish coast fishing, including what3words emergency location guidance, coastal safety tips, and fishing alone precautions."
        keywords="what3words, angler safety, coastal safety, fishing safety tips, Cornish coast fishing, sea angling safety, emergency location guidance, Redruth Sea Angling Association"
        url="https://www.redruthseaanglingassociation.co.uk/angler-safety"
        image="https://www.redruthseaanglingassociation.co.uk/assets/scenic_background.png"
      />
      <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="mx-auto max-w-3xl px-6 py-16 sm:px-8 lg:px-12">

        <Link
          to="/"
          className="standalone-only inline-flex items-center gap-2 rounded-lg border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200 transition hover:bg-cyan-400/20"
        >
          ← Back
        </Link>

        <div className="mt-8 flex items-start gap-4">
          <div className="shrink-0 rounded-xl border border-red-400/20 bg-red-500/10 p-3">
            <ShieldAlert className="h-6 w-6 text-red-400" />
          </div>
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.3em] text-red-400">Angler Safety</p>
            <h1 className="heading-glow mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl">Know Your Location</h1>
            <p className="mt-4 text-base leading-7 text-slate-400">
              Practical advice on making sure you can always be found in an emergency — wherever you are fishing along the Cornish coast.
            </p>
          </div>
        </div>

        <div className="mt-12 space-y-8">

          {/* Why it matters */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8 panel-lift">
            <h2 className="text-lg font-bold text-white mb-3">Why local marks can be a problem</h2>
            <p className="text-sm leading-7 text-slate-300">
              Many of the best fishing marks along the Cornish coast are known only by local names — names that coastguard
              and rescue services may never have heard of. If you need to make an emergency call, simply saying "I'm at
              Deadman's Cove" or "the rocks below the old mine" may not be enough for help to reach you quickly.
              A precise location, given immediately, could make all the difference.
            </p>
          </div>

          {/* what3words */}
          <div className="rounded-2xl border border-red-400/20 bg-red-500/5 p-6 sm:p-8 panel-lift">
            <h2 className="text-lg font-bold text-white mb-3">what3words</h2>
            <p className="text-sm leading-7 text-slate-300">
              <strong className="text-white">what3words</strong> divides the entire world into 3&nbsp;m × 3&nbsp;m squares,
              each with a unique three-word address — for example <span className="font-mono text-cyan-300">{'///'}filled.count.soap</span>.
              Emergency services across the UK, including <strong className="text-white">HM Coastguard</strong> and all
              UK police forces, now accept what3words locations. It works even in areas with no street address and with
              minimal mobile signal.
            </p>
            <a
              href="https://what3words.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2.5 text-sm font-semibold text-red-300 transition hover:bg-red-500/20"
            >
              <MapPin className="h-4 w-4 shrink-0" />
              Visit what3words.com
            </a>
          </div>

          {/* Tips */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8 panel-lift">
            <h2 className="text-lg font-bold text-white mb-5">Before you head out</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-400/15 text-sm font-bold text-red-300">1</span>
                <p className="text-sm leading-7 text-slate-300">
                  Open the <strong className="text-white">what3words app</strong> (free on iOS and Android) or visit{" "}
                  <a href="https://what3words.com" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">what3words.com</a>{" "}
                  when you arrive at your mark. Note the three-word address shown for your exact spot.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-400/15 text-sm font-bold text-red-300">2</span>
                <p className="text-sm leading-7 text-slate-300">
                  <strong className="text-white">Share it before you fish</strong> — send the three-word address to a
                  family member or friend so they know exactly where you are. If you are not back when expected, they
                  can pass it straight to the rescue services without delay.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-400/15 text-sm font-bold text-red-300">3</span>
                <p className="text-sm leading-7 text-slate-300">
                  This is especially important if you are <strong className="text-white">fishing alone</strong>. Solo
                  angling on rocky or remote marks carries real risk — a simple three-word address shared in advance
                  costs nothing and could save your life.
                </p>
              </div>
            </div>
          </div>

          {/* Emergency reminder */}
          <div className="rounded-2xl border border-amber-400/20 bg-amber-500/5 px-6 py-5 panel-lift">
            <p className="text-sm font-semibold text-amber-300 mb-1">In an emergency</p>
            <p className="text-sm leading-6 text-slate-300">
              Call <strong className="text-white">999</strong> and ask for the Coastguard. Give them your what3words
              address as well as a description of your situation. Stay on the line and follow their instructions.
            </p>
          </div>

        </div>
      </div>
    </div>
    </>
  );
}
